import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { MapLink, User } from '../types'
import { fetchWithAuth } from '../utils/api'
import WildlifeObservation from './WildlifeObservation'
import WildlifeView from './WildlifeView'
import DashboardCard from './DashboardCard'
import QuickStats from './QuickStats'
import ActivityFeed from './ActivityFeed'

interface UserViewProps {
  user: User
}

function UserView({ user }: UserViewProps) {
  const navigate = useNavigate()
  const [mapLinks, setMapLinks] = useState<MapLink[]>([])
  const [filteredLinks, setFilteredLinks] = useState<MapLink[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [showWildlifeObservation, setShowWildlifeObservation] = useState(false)
  const [showWildlifeView, setShowWildlifeView] = useState(false)
  const [mapLinksExpanded, setMapLinksExpanded] = useState(false)
  const [taskCount, setTaskCount] = useState(0)
  const [assetCount, setAssetCount] = useState(0)
  const [currentTime, setCurrentTime] = useState(() => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const hours = String(now.getHours()).padStart(2, '0')
    const minutes = String(now.getMinutes()).padStart(2, '0')
    const seconds = String(now.getSeconds()).padStart(2, '0')
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
  })

  useEffect(() => {
    fetchMapLinks()
    fetchCounts()
  }, [])

  useEffect(() => {
    const updateTime = () => {
      const now = new Date()
      const year = now.getFullYear()
      const month = String(now.getMonth() + 1).padStart(2, '0')
      const day = String(now.getDate()).padStart(2, '0')
      const hours = String(now.getHours()).padStart(2, '0')
      const minutes = String(now.getMinutes()).padStart(2, '0')
      const seconds = String(now.getSeconds()).padStart(2, '0')
      
      setCurrentTime(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`)
    }

    const interval = setInterval(updateTime, 1000)
    
    return () => clearInterval(interval)
  }, [])

  const fetchCounts = async () => {
    try {
      const [tasksResponse, assetsResponse] = await Promise.all([
        fetchWithAuth('/api/tasks').catch(() => null),
        fetchWithAuth('/api/assets').catch(() => null)
      ])

      if (tasksResponse && tasksResponse.ok) {
        const tasksData = await tasksResponse.json()
        const activeTasks = (tasksData.tasks || []).filter((task: any) => task.status === 'active')
        setTaskCount(activeTasks.length)
      }

      if (assetsResponse && assetsResponse.ok) {
        const assetsData = await assetsResponse.json()
        setAssetCount(assetsData.assets?.length || 0)
      }
    } catch (error) {
      console.error('Failed to fetch counts:', error)
    }
  }

  const getCurrentTime = () => {
    return currentTime
  }

  useEffect(() => {
    if (!searchTerm) {
      setFilteredLinks(mapLinks)
    } else {
      const filtered = mapLinks.filter(link =>
        link.site_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.area_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.pilot_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        link.comment.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredLinks(filtered)
    }
  }, [mapLinks, searchTerm])

  const fetchMapLinks = async () => {
    try {
      const response = await fetchWithAuth('/api/map-links')
      if (!response.ok) {
        throw new Error('Failed to fetch map links')
      }
      const data = await response.json()
      setMapLinks(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load map links')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toISOString().slice(0, 19).replace('T', ' ')
  }

  if (loading) {
    return <div className="loading">Loading map links...</div>
  }

  if (error) {
    return (
      <div>
        <div className="error">{error}</div>
        <button className="btn" onClick={fetchMapLinks}>
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="modern-dashboard">
      {showWildlifeObservation && (
        <WildlifeObservation
          onClose={() => setShowWildlifeObservation(false)}
          onSuccess={() => {
            setShowWildlifeObservation(false)
            fetchCounts() // Refresh counts after new observation
          }}
        />
      )}

      {showWildlifeView && (
        <WildlifeView
          onClose={() => setShowWildlifeView(false)}
        />
      )}

      {/* Hero Section */}
      <div className="dashboard-hero">
        <div className="hero-header">
          <div className="hero-welcome">
            <h1>Welcome back, {user.fullName || user.username}!</h1>
            <p className="hero-subtitle">
              Manage your farm operations efficiently with FarmCore
            </p>
          </div>
          <div className="hero-meta">
            <div className="current-time">
              📅 {getCurrentTime()}
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <QuickStats />
      </div>

      {/* Primary Action Cards with Quick Actions */}
      <div className="dashboard-grid dashboard-grid--primary">
        <DashboardCard
          icon="📋"
          title="Farm Tasks"
          subtitle="Create, manage, and track all farm operations"
          count={taskCount > 0 ? taskCount : undefined}
          onClick={() => navigate('/tasks')}
          variant="primary"
          quickActions={[
            { label: 'New Task', icon: '➕', onClick: () => navigate('/tasks?new=1') }
          ]}
        />
        
        <DashboardCard
          icon="📦"
          title="Assets"
          subtitle="Manage livestock, crops, and infrastructure"
          count={assetCount > 0 ? assetCount : undefined}
          onClick={() => navigate('/assets')}
          variant="secondary"
          quickActions={[
            { label: 'Add Asset', icon: '➕', onClick: () => navigate('/assets') }
          ]}
        />
        
        <DashboardCard
          icon="🗺️"
          title="Site Map"
          subtitle="View boundaries and live activity"
          onClick={() => navigate('/site-map')}
          variant="secondary"
          quickActions={[
            { label: 'Open Map', icon: '🗺️', onClick: () => navigate('/site-map') }
          ]}
        />

        <DashboardCard
          icon="🦌"
          title="Wildlife Data"
          subtitle="Browse observations, maps & recent activity"
          onClick={() => setShowWildlifeView(true)}
          variant="secondary"
          quickActions={[
            { label: 'Quick Observation', icon: '📍', onClick: () => setShowWildlifeObservation(true) },
            { label: 'View Data', icon: '📊', onClick: () => setShowWildlifeView(true) }
          ]}
        />
        
        {user.isAdmin && (
          <DashboardCard
            icon="🔧"
            title="Admin Dashboard"
            subtitle="System administration and user management"
            onClick={() => navigate('/admin')}
            variant="admin"
          />
        )}
      </div>

      {/* Secondary Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: mapLinksExpanded ? '1fr' : '2fr 1fr', gap: 'var(--spacing-md)', alignItems: 'start' }}>
        
        {/* Drone Photos Widget */}
        <div className="map-links-widget">
          <div className="map-links-widget__header">
            <h3 className="map-links-widget__title">🚁 Drone Photos</h3>
            <button 
              className="map-links-widget__toggle"
              onClick={() => setMapLinksExpanded(!mapLinksExpanded)}
            >
              {mapLinksExpanded ? 'Show Less' : 'Show All'}
            </button>
          </div>
          
          {!mapLinksExpanded ? (
            <div className="map-links-widget__preview">
              {loading ? (
                <div className="loading-spinner">Loading drone photos...</div>
              ) : filteredLinks.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#666', margin: '1rem' }}>
                  No drone photos available
                </p>
              ) : (
                <div className="map-links-preview">
                  {filteredLinks.slice(0, 3).map((link) => (
                    <div key={link.id} className="map-link-preview-item">
                      <div className="map-link-preview-item__info">
                        <div className="map-link-preview-item__title">
                          {link.site_name} - {link.area_name}
                        </div>
                        <div className="map-link-preview-item__meta">
                          {formatDate(link.date_time)} • {link.pilot_name}
                        </div>
                      </div>
                      <div className="map-link-preview-item__action">
                        <button 
                          className="btn"
                          onClick={() => window.open(link.url, '_blank')}
                          style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                        >
                          🚁
                        </button>
                      </div>
                    </div>
                  ))}
                  {filteredLinks.length > 3 && (
                    <div style={{ textAlign: 'center', margin: '0.5rem 0', fontSize: '0.8rem', color: '#666' }}>
                      +{filteredLinks.length - 3} more photos
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div style={{ padding: 'var(--spacing-md)' }}>
              <div style={{ marginBottom: 'var(--spacing-md)' }}>
                <input
                  type="text"
                  placeholder="Search by site, area, pilot, or comment..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #ddd' }}
                />
              </div>

              {filteredLinks.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                  {mapLinks.length === 0 ? 'No drone photos available' : 'No matches found'}
                </div>
              ) : (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Site</th>
                          <th>Area</th>
                          <th>Drone Photo</th>
                          <th>Date/Time</th>
                          <th>Pilot</th>
                          <th>Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLinks.map((link) => (
                          <tr key={link.id}>
                            <td><span className="site-badge">{link.site_name}</span></td>
                            <td><span className="area-badge">{link.area_name}</span></td>
                            <td className="link-cell">
                              <button 
                                className="btn"
                                onClick={() => window.open(link.url, '_blank')}
                                title={link.url}
                                style={{ fontSize: '0.9rem', padding: '0.3rem 0.6rem' }}
                              >
                                🚁 View Photo
                              </button>
                            </td>
                            <td className="date-cell">{formatDate(link.date_time)}</td>
                            <td><span className="pilot-badge">{link.pilot_name}</span></td>
                            <td style={{ fontStyle: 'italic', color: '#666' }}>{link.comment}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#666', textAlign: 'center' }}>
                    Showing {filteredLinks.length} of {mapLinks.length} drone photos
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Activity Feed */}
        {!mapLinksExpanded && (
          <ActivityFeed maxItems={5} />
        )}
        
      </div>
    </div>
  )
}

export default UserView
